'use client';

import React, { useState, useEffect } from 'react';
import { fetchRevenuePerformanceDashboard } from '../../apiHelper';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { TrendingUp, TrendingDown, Calendar, Store, Package, RefreshCcw } from 'lucide-react';

const formatCurrency = (val) => {
    if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `₦${(val / 1000).toFixed(1)}K`;
    return `₦${val?.toLocaleString()}`;
};

const RevenueDashboardSection = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        start_date: '2026-03-01',
        end_date: '2026-04-13',
        store_id: null,
        category_id: null,
    });

    const [activeTab, setActiveTab] = useState('trend');

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const result = await fetchRevenuePerformanceDashboard(filters);
                setData(result);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [filters]);

    // --- PULSATING SKELETON UI ---
    if (loading) return (
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 h-full flex flex-col animate-pulse">
            {/* Header Skeleton */}
            <div className="flex items-center justify-between mb-8">
                <div className="space-y-3">
                    <div className="h-9 w-64 bg-gray-200 rounded-xl" />
                    <div className="h-3 w-48 bg-gray-100 rounded-lg" />
                </div>
                <div className="flex gap-4">
                    <div className="h-12 w-64 bg-gray-100 rounded-2xl" />
                    <div className="h-12 w-32 bg-gray-200 rounded-2xl" />
                </div>
            </div>

            {/* KPI Grid Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <div className="h-32 bg-blue-100 rounded-3xl" />
                <div className="h-32 bg-gray-50 rounded-3xl border border-gray-100" />
                <div className="h-32 bg-gray-50 rounded-3xl border border-gray-100" />
                <div className="h-32 bg-gray-50 rounded-3xl border border-gray-100" />
            </div>

            {/* Content Skeleton */}
            <div className="flex-1">
                <div className="flex gap-8 border-b mb-6 pb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded" />
                    <div className="h-6 w-32 bg-gray-100 rounded" />
                    <div className="h-6 w-32 bg-gray-100 rounded" />
                </div>
                <div className="h-[380px] bg-gray-50 rounded-3xl border border-dashed border-gray-200" />
            </div>
        </div>
    );

    const revenue = data?.total_revenue || {};
    const growth = revenue.growth_percent || 0;
    const isPositive = growth >= 0;

    return (
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Revenue Performance</h2>
                    <p className="text-gray-500 text-xs mt-1">Real-time insights • Powered by your transactions</p>
                </div>

                <div className="flex gap-4 items-center">
                    <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-2xl">
                        <Calendar className="w-5 h-5 text-[#4285F4]" />
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                            className="bg-transparent outline-none text-sm"
                        />
                        <span className="text-gray-400">→</span>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                            className="bg-transparent outline-none text-sm"
                        />
                    </div>

                    <select
                        className="bg-gray-50 px-5 py-3 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4285F4]"
                        onChange={(e) => setFilters({
                            ...filters,
                            store_id: e.target.value ? parseInt(e.target.value) : null
                        })}
                    >
                        <option value="">All Stores</option>
                    </select>

                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-[#4285F4] text-white rounded-2xl hover:bg-blue-600 transition font-medium flex items-center gap-2"
                    >
                        <RefreshCcw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <div className="bg-gradient-to-br from-[#4285F4] to-blue-600 text-white p-7 rounded-3xl">
                    <p className="text-blue-100 text-sm font-medium">TOTAL REVENUE</p>
                    <p className="text-3xl font-bold mt-3 tracking-tighter leading-none">
                        {formatCurrency(revenue.current_period_revenue)}
                    </p>
                    <div className={`mt-6 flex items-center gap-2 text-sm ${isPositive ? 'text-emerald-300' : 'text-red-300'}`}>
                        {isPositive ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                        <span className="font-semibold">{growth.toFixed(1)}%</span>
                        <span className="text-blue-100 text-xs">from previous</span>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl">
                    <p className="text-gray-500 text-sm font-medium">DAILY AVERAGE</p>
                    <p className="text-3xl font-bold mt-3 tracking-tighter leading-none text-gray-800">
                        {formatCurrency(revenue.daily_average)}
                    </p>
                    <p className="text-xs text-gray-400 mt-6">Average revenue per day</p>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl">
                    <p className="text-gray-500 text-sm font-medium">GROWTH RATE</p>
                    <p className={`text-4xl font-bold mt-3 tracking-tighter leading-none ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                        {growth.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-400 mt-6">Period over period</p>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl flex flex-col justify-between">
                    <div>
                        <p className="text-gray-500 text-sm font-medium">TOTAL ORDERS</p>
                        <p className="text-4xl font-bold mt-3 tracking-tighter text-gray-800">
                            {data?.total_revenue?.order_count || 0}
                        </p>
                    </div>
                    <p className="text-[#4285F4] text-sm font-medium cursor-pointer hover:underline">View orders →</p>
                </div>
            </div>

            {/* Content Tabs Area */}
            <div className="flex-1 flex flex-col">
                <div className="flex border-b mb-6">
                    {[
                        { key: 'trend', label: 'Revenue Trend', icon: LineChart },
                        { key: 'skus', label: 'Top Products', icon: Package },
                        { key: 'stores', label: 'By Store', icon: Store },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`px-8 py-4 font-medium flex items-center gap-3 border-b-2 transition-all ${activeTab === tab.key
                                ? 'border-[#4285F4] text-[#4285F4]'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <tab.icon size={20} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'trend' && data?.revenue_trend_30d && (
                    <div className="bg-white border border-gray-100 rounded-3xl p-6 h-[380px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.revenue_trend_30d}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="date" stroke="#888" fontSize={12} />
                                <YAxis stroke="#888" fontSize={12} />
                                <Tooltip
                                    contentStyle={{ border: 'none', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                />
                                <Line type="natural" dataKey="value" stroke="#4285F4" strokeWidth={4} dot={{ fill: '#4285F4', r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
                {/* ... (Other tabs follow the same data-check pattern) */}
            </div>

            {/* Smart Insights Footer */}
            {data?.insights && (
                <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-3xl p-6">
                    <h4 className="font-semibold text-[#4285F4] mb-4 flex items-center gap-2">💡 Smart Insights</h4>
                    <ul className="space-y-3">
                        {data.insights.map((insight, i) => (
                            <li key={i} className="text-gray-700 pl-6 relative before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:bg-[#4285F4] before:rounded-full">
                                {insight}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default RevenueDashboardSection;